import { useState } from 'react'

import { isAppError } from '@/shared/api'

/**
 * Runs an action that goauth may refuse until the password is re-entered, and
 * replays it once that happens.
 *
 * The sudo window is not something the client can check in advance — it depends
 * on a claim inside a token the panel does not decode, and it expires while the
 * page is open. So the refusal is the trigger: attempt, catch the one code that
 * means "prove it again", and retry after the dialog succeeds. Asking for the
 * password up front would demand it on every action, most of which are inside
 * the window and need nothing.
 */
export function useSudoGuard() {
  const [pendingAction, setPendingAction] = useState<(() => Promise<void>) | null>(null)

  /**
   * Resolves `true` when the action went through, `false` when goauth asked for
   * the password and it was deferred to the dialog.
   *
   * The answer matters because a refusal is not a failure and not a success: it
   * is "not yet". Reporting it as success closed the set-password dialog and
   * cleared the field while the prompt behind it was still waiting — and if the
   * reader then dismissed the prompt, nothing had happened and nothing said so.
   */
  const runGuarded = async (action: () => Promise<void>): Promise<boolean> => {
    try {
      await action()
      return true
    } catch (error) {
      if (isAppError(error) && error.code === 'GOAUTH_REAUTH_REQUIRED') {
        // Stored behind a thunk: `setState` treats a bare function as an updater.
        setPendingAction(() => action)
        return false
      }
      throw error
    }
  }

  const replay = () => {
    const action = pendingAction
    setPendingAction(null)
    if (action !== null) void runGuarded(action)
  }

  return {
    runGuarded,
    isSudoOpen: pendingAction !== null,
    closeSudo: () => setPendingAction(null),
    confirmSudo: replay,
  }
}
